import { FileText, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, fetchFile } from '../lib/api';
import { fileSize, prepareSample } from '../lib/samples';
import type { Child, WorkSample } from '../lib/types';
import { useParent } from './ParentContext';
import { canDo, childHas } from './Plans';

/**
 * Work samples (#603) on an assignment or a day's journal entry: photos and PDFs of the child's work, each marked
 * for the portfolio or not. Students can add them when they turn work in; parents add, mark and remove them here.
 */
export function WorkSamples({
  child,
  plannerItemId,
  assignmentId,
  journalDate,
  portfolio = false,
}: {
  child: Child;
  plannerItemId?: string;
  /** A grade-book-only assignment (#637). */
  assignmentId?: string;
  journalDate?: string;
  /** On a Portfolio-type assignment (#637): titled as the work for the portfolio. */
  portfolio?: boolean;
}) {
  const { toast } = useParent();
  const [samples, setSamples] = useState<WorkSample[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const input = useRef<HTMLInputElement>(null);
  const allowed = childHas(child, 'portfolio') && canDo(child, 'planner', 'view');
  const canEdit = canDo(child, 'planner', 'edit');
  const query = plannerItemId
    ? `plannerItemId=${encodeURIComponent(plannerItemId)}`
    : assignmentId
      ? `assignmentId=${encodeURIComponent(assignmentId)}`
      : `journalDate=${journalDate}`;

  const load = useCallback(() => {
    if (!allowed) return;
    api
      .get<{ samples: WorkSample[] }>(`/api/children/${child.id}/samples?${query}`)
      .then((r) => setSamples(r.samples))
      .catch(() => setSamples([]));
  }, [allowed, child.id, query]);
  useEffect(load, [load]);

  // Pictures are fetched with the sign-in and shown from memory.
  useEffect(() => {
    let live = true;
    const made: string[] = [];
    for (const s of (samples ?? []).filter((x) => x.mime.startsWith('image/') && !thumbs[x.id])) {
      fetchFile(`/api/samples/${s.id}`)
        .then((b) => {
          const u = URL.createObjectURL(b);
          made.push(u);
          if (live) setThumbs((cur) => ({ ...cur, [s.id]: u }));
        })
        .catch(() => undefined);
    }
    return () => {
      live = false;
    };
  }, [samples]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!allowed || !samples) return null;

  async function add(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true);
    try {
      const ready = [];
      for (const f of [...files].slice(0, 10)) ready.push(await prepareSample(f, 25));
      await api.post(`/api/children/${child.id}/samples`, { ...(plannerItemId ? { plannerItemId } : assignmentId ? { assignmentId } : { journalDate }), files: ready.map(({ name, data }) => ({ name, data })) });
      toast({ text: ready.length === 1 ? 'Added. It’s in the portfolio.' : `Added ${ready.length}. They’re in the portfolio.`, tone: 'good' });
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }
  async function toggle(s: WorkSample) {
    try {
      const next = await api.patch<WorkSample>(`/api/samples/${s.id}`, { inPortfolio: !s.inPortfolio });
      setSamples((list) => list?.map((x) => (x.id === s.id ? next : x)) ?? null);
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  async function remove(s: WorkSample) {
    if (!confirm(`Remove ${s.name}? It can’t be brought back.`)) return;
    try {
      await api.del(`/api/samples/${s.id}`);
      setSamples((list) => list?.filter((x) => x.id !== s.id) ?? null);
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  async function open(s: WorkSample) {
    try {
      const url = URL.createObjectURL(await fetchFile(`/api/samples/${s.id}`));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  const inPortfolio = samples.filter((s) => s.inPortfolio).length;
  return (
    <div className={`stack work-samples${portfolio ? ' portfolio-work' : ''}`}>
      <div className="row-between">
        <strong>{portfolio ? 'Work for the portfolio' : 'Work samples'}</strong>
        {samples.length > 0 && <span className="muted small">{inPortfolio} in portfolio</span>}
      </div>
      <div className="sample-grid">
        {samples.map((s) => (
          <div key={s.id} className={`sample-card ${s.inPortfolio ? 'on' : ''}`}>
            <button type="button" className="sample-thumb" onClick={() => open(s)} aria-label={`Open ${s.name}`}>
              {s.mime.startsWith('image/') ? thumbs[s.id] ? <img src={thumbs[s.id]} alt="" /> : <span className="muted small">Loading…</span> : <FileText size={34} aria-hidden />}
            </button>
            <div className="sample-meta">
              <span className="small sample-name" title={s.name}>
                {s.name}
              </span>
              <span className="muted small">
                {fileSize(s.size)}
                {s.addedBy === 'student' ? ` · from ${child.name}` : ''}
              </span>
              {canEdit && (
                <div className="row-gap">
                  <button type="button" className={`chip small ${s.inPortfolio ? 'active' : ''}`} aria-pressed={s.inPortfolio} onClick={() => toggle(s)}>
                    {s.inPortfolio ? '✓ In portfolio' : 'Add to portfolio'}
                  </button>
                  <button type="button" className="icon-btn" onClick={() => remove(s)} aria-label={`Remove ${s.name}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        {canEdit && (
          <button type="button" className="sample-add" disabled={busy} onClick={() => input.current?.click()}>
            <Plus size={22} aria-hidden />
            {busy ? 'Adding…' : 'Add a photo or file'}
            <span className="muted small">A photo of the work, or a PDF</span>
          </button>
        )}
      </div>
      <input ref={input} type="file" accept="image/*,application/pdf" multiple hidden onChange={(e) => void add(e.target.files)} />
    </div>
  );
}
